import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { existsFilter } from '@iotv/core-services';
import { getObjectHash } from '@iotv/datamodel-core';
import { isVOBofType, onOff, ValidObjectDeduper } from '@iotv/iotv-v3-types';
import { Grid, Skeleton, Stack, Typography } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { AppMainViewContext } from '../../../ContextProviders/AppContextProvider';
import { GoogleMapAPIMapTypeIds } from '../../../GoogleMapV4';
import { NullLabel } from '../../../GoogleMapV4/components/Labels/NullLabel';
import { generatedAreaAwareStleFn } from '../../../GoogleMapV4/themesAndStyles/defaultStyleFns';
import { useThemedMap } from '../../../GoogleMapV4/useThemedMap';
import { getNiceDate } from '../../../data/TypeHelpers';
import { CoverageReportContext } from '../Contexts';
import { NetworkCoverageContext } from '../NetworkCoverageContext';
import { RequestStatusIndicator } from '../ResultRetrieval/RequestStatusIndicator';
import { getCoverageReportSk } from '../ResultRetrieval/hooks/useCoverage';
import { populateNetworkCoverageContextFromProperty } from '../Selection/queries/addAreasPlacesToContext';
import { LabelArbitrator } from '../subComponents/LabelArbitrator';
import { getGatewayReceptionLineStyleFn, getReceptionAreaStyleFn } from './FeatureStyles';
import { coverageGeoObjectsToValidGeoObjects, coverageReportToGeoObject, coverageReportToLineDefiningPairs } from './mapFns';
const debug = process.env.REACT_APP_DEBUG && false;
export const NetworkCoverageMap = ({ singleContextObject }) => {
    /** TODO default bound must change depending on user customer context */
    const canaryString = 'NetworkCoverageMap';
    debug && console.log(`${canaryString} called with`, { singleContextObject });
    // main app state
    const [{ isSmallView, TransactionFunctions }] = useContext(AppMainViewContext);
    // coverage state
    const { state: coverageState, dispatch: coverageDispatch } = useContext(NetworkCoverageContext);
    const { nodeEdgeMapper, areaData, lineDefiningPairs, Areas, Gateways, Networks, Places, Properties, bounds, nemHash, requestStatusMap } = coverageState;
    // coverage wizard (steps) state
    const { lineColorMetricAttribute, receptionAreaColorMetricAttribute, activeStep } = coverageState;
    // all reports state
    const coverageReportState = useContext(CoverageReportContext);
    const { coverageReportMap, selectedReports } = coverageReportState;
    const [lineColorFn, setLineColorFn] = useState(() => getGatewayReceptionLineStyleFn(lineColorMetricAttribute));
    const [receptionAreaColorFn, setReceptionAreaColorFn] = useState(() => getReceptionAreaStyleFn(receptionAreaColorMetricAttribute));
    const [AreaStyleFn] = useState(() => generatedAreaAwareStleFn);
    const [AreaLabelFn] = useState(() => LabelArbitrator);
    const dataFn = (async (vob) => {
        return { ...vob, metadata: { ...vob.metadata, castFromType: vob.type }, data: vob.data };
    });
    debug && console.log(`NW MAP state areaData`, areaData);
    const { map: { current: mapElement }, state: { areaDataAttributeKey, pathDataAttributeKey }, quarantinedItemsDialog, components: { MapLegend, MapDatalayerVisibility, PortalDiv }, } = useThemedMap({
        showDrawingTools: false,
        capabilities: {
            edit: onOff.OFF,
        },
        themesAndLabels: {
            theme: 'coverage', // WEIRD(aidan): theme isn't really used internally at all, instead we manage the color fn ourselves in this file
            areaDataAttributeKey: receptionAreaColorMetricAttribute,
            pathAttributeKey: undefined,
            pathDataAttributeKey: lineColorMetricAttribute,
            rangeColorMaps: undefined, // WEIRD(aidan): Despite managing color ourselves, coverage range maps *are* included in default useThemedMap
            Area: { styleFn: AreaStyleFn, LabelComponent: AreaLabelFn },
            Path: undefined,
            PathData: { styleFn: lineColorFn, LabelComponent: NullLabel },
            AreaDatas: { styleFn: receptionAreaColorFn, LabelComponent: NullLabel },
            Place: { LabelComponent: AreaLabelFn },
        },
        canaryString: 'NetworkCoverage',
        functions: {},
        options: {
            mapTypeId: GoogleMapAPIMapTypeIds.terrain,
            mapTypeControl: true,
            controlSize: 24,
            tilt: 0,
            zoomControl: true,
            scaleControl: true,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            disableDefaultUI: isSmallView,
            mapTypeControlOptions: {
                style: isSmallView ? google.maps.MapTypeControlStyle.DROPDOWN_MENU : google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                mapTypeIds: [GoogleMapAPIMapTypeIds.satellite, GoogleMapAPIMapTypeIds.terrain],
                position: google.maps.ControlPosition.BOTTOM_LEFT,
            },
        },
        drawingManagerOptions: {
            drawingControlOptions: {
                position: google.maps.ControlPosition.BOTTOM_RIGHT,
            },
        },
        transactionFunctions: TransactionFunctions,
        contextObject: null,
        dataFn,
        /** these are mutated to force rerender, because they are not in NodeEdgeMapperContext. UseThemedMap cannot yet depend on scope beyond NodeEdgeMapper.
         *  That is kind of entirely the point */
        areaData,
        lineDefiningPairs,
        nodeEdgeMapper,
        nodeEdgeMapperDispatch: coverageDispatch,
    });
    /** singleContextObject use effect */
    useEffect(() => {
        if (singleContextObject) {
            if (isVOBofType('Property', singleContextObject)) {
                console.warn(`WARN!!! NOT setBounds setNetworkCoverageContext(updater)`);
                populateNetworkCoverageContextFromProperty({ dispatch: coverageDispatch, properties: [singleContextObject] });
            }
            else {
                debug && console.warn(`WARN: PropertySelector has singleContextObject not of type`);
            }
        }
    }, [singleContextObject?.sk]);
    /** MetricKey Use Effect */
    useEffect(() => {
        debug && console.log('NetworkCoverageMap FeatureStyles MetricKey useEffect', areaDataAttributeKey, pathDataAttributeKey);
        try {
            if (areaDataAttributeKey)
                setReceptionAreaColorFn(() => getReceptionAreaStyleFn(areaDataAttributeKey));
            if (pathDataAttributeKey)
                setLineColorFn(() => getGatewayReceptionLineStyleFn(pathDataAttributeKey));
        }
        catch (e) {
            debug && console.log('NetworkCoverageMap FeatureStyles MetricKey useEffect caught error', lineColorMetricAttribute, receptionAreaColorMetricAttribute, e);
        }
    }, [areaDataAttributeKey, pathDataAttributeKey]);
    /**
     * Coverage display
     * when selected areas and places (geoObjects) change, the input to coverage hook is mutated with only the geoObject selected
     * This may be better as a useEffect if rerendering on some other influence
     * Coverage reports are displayed on a separate layer from Areas and Place, so the area and place objects do not need stats injected. State are part of coverage reports layer
     */
    const geoObjectIsSelected = (pk) => [...Areas, ...Places].find((geoObject) => geoObject.pk === pk);
    const filteredCoverageReports = Object.entries(coverageReportMap).filter(([geoObjectSk, nwReportsMap]) => geoObjectIsSelected(geoObjectSk));
    const filteredCoverageGeoObjects = filteredCoverageReports
        .map(([nwID, reports]) => reports
        .filter((coverageReport) => selectedReports.includes(getCoverageReportSk(coverageReport)))
        .map((cr) => coverageReportToGeoObject({ cr, Networks, Gateways })))
        .flat();
    debug && console.log(`filteredCoverageGeoObjects`, filteredCoverageGeoObjects);
    const filteredCoverageGeoObjectsHash = getObjectHash(filteredCoverageGeoObjects);
    /** provide a list of defining objects to the layer so that they can be matched with with the feature, then converted into an element that renders some information derived from the object */
    useEffect(() => {
        const data = coverageGeoObjectsToValidGeoObjects(filteredCoverageGeoObjects);
        const gridWithData = filteredCoverageReports
            .flat(2)
            .filter((keyOrReport) => typeof keyOrReport === 'object')
            .map((report) => report.gridsWithData)
            .flat()
            .filter(existsFilter);
        debug && console.log(`gridWithData`, gridWithData);
        /** FUTURE - It may be possible to style individual cells of a multipolygon which would considerably improve rendering of large grids*/
        const areaDataFromGrids = gridWithData.map((grid) => grid.cells).flat();
        const addedAreaData = data.filter((d) => !gridWithData.find((g) => g.generatedFrom === d?.metadata?.generatedFrom));
        debug && console.log(`addedAreaData`, addedAreaData);
        const action = { type: 'setAreaData', areaDatas: ValidObjectDeduper([...addedAreaData, ...areaDataFromGrids], false) };
        debug && console.log('SetAreaDataAction', action);
        coverageDispatch(action);
        debug &&
            console.log(`NW Coverage useCoverage used effect on ${Object.keys(coverageReportMap).join(', ')} filteredCoverageReports ${filteredCoverageGeoObjects.map((d) => d.sk).join(', ')} `, action);
    }, [filteredCoverageGeoObjectsHash]);
    const filteredCoverageReportsHash = getObjectHash(Object.keys(filteredCoverageReports));
    // /** get all the source/target pairs plus label and styleFns from every current coverage report, then display via useLines */
    useEffect(() => {
        const lineDefiningPairs = filteredCoverageReports
            .map(([nwID, reports]) => reports
            .filter((coverageReport) => selectedReports.includes(getCoverageReportSk(coverageReport)))
            .map((report) => coverageReportToLineDefiningPairs(report, Networks))
            .flat())
            .flat();
        debug && console.log(`NetworkMap: lineDefinitions has ${lineDefiningPairs.length} items with ${Networks ? Networks.join(', ') : 'Networks is undefined!'}`);
        const action = { type: 'setLineDefiningPairs', lineDefiningPairs };
        coverageDispatch(action);
    }, [filteredCoverageReportsHash]);
    const debugDump = (_jsxs(Stack, { gap: 1, children: [_jsx(Typography, { children: `Places: ${Object.values(Places)
                    .map((place) => place.sk + ` (${place.targetESP})`)
                    .join(', ')}` }, "places"), _jsx(Typography, { children: `Areas: ${Object.values(Areas)
                    .map((area) => area.sk + ` (${area.targetESP})`)
                    .join(', ')}` }, "areas"), _jsx(Typography, { children: `Gateways: ${Object.values(Gateways)
                    .map((gateway) => gateway.sk)
                    .join(', ')}` }, "gateways"), _jsx(Typography, { children: `Networks: ${Object.values(Networks)
                    .map((network) => network)
                    .join(', ')}` }, "networks"), _jsx(Typography, { children: `AreaData: ${Object.values(areaData)
                    .map((data) => data.sk)
                    .join(', ')}` }, "areadata"), _jsx(Typography, { children: `Period (from, to): ${JSON.stringify({ from: getNiceDate(coverageState.parameters.timePeriod?.fromDateMs ?? -1), to: getNiceDate(coverageState.parameters.timePeriod?.toDateMs ?? -1) })}` }, "period"), _jsx(Typography, { children: `CoverageMapKeys: ${Object.keys(coverageReportMap).join(', ')}` }, "keys"), _jsx(Typography, { children: `CoverageMaps: ${Object.values(coverageReportMap)
                    .flat()
                    .map((report) => `${report.GeoObject.name} [${report.created}]`)
                    .join(', ')}` }, "maps")] }, "debugStack"));
    const direction = isSmallView ? 'column' : 'row';
    const controlsMargin = 12;
    const controlsWidth = isSmallView ? '250px' : `calc(100% - ${2 * controlsMargin}px)`;
    const justifyContent = isSmallView ? undefined : 'flex-end';
    const spacing = isSmallView ? 0 : 1;
    const controlsStack = (_jsxs(Stack, { width: controlsWidth, spacing, direction, sx: { zIndex: 2, height: 0, position: 'relative', top: controlsMargin, left: controlsMargin }, children: [_jsx(Stack, { direction, spacing, width: isSmallView ? undefined : '75%', children: _jsx(Stack, { id: "datalayerVisibility", children: MapDatalayerVisibility }, "datalayerVisibility") }), _jsx(Stack, { direction, width: isSmallView ? '100%' : '25%', justifyContent, children: _jsx(Stack, { id: "legend", children: MapLegend }, "legend") })] }, "controls"));
    return (_jsxs(_Fragment, { children: [debug && debugDump, _jsxs(Grid, { container: true, spacing: 1, size: 12, width: '100%', children: [0 < activeStep && 0 < Object.values(requestStatusMap).length && (_jsx(Grid, { size: 12, children: _jsx(RequestStatusIndicator, { withProgressBar: false }) }, "EA")), _jsxs(Grid, { size: 12, sx: { minHeight: isSmallView ? '60cqh' : '70cqh' }, children: [controlsStack, mapElement, !mapElement && _jsx(Skeleton, { animation: 'wave', variant: 'rectangular', height: isSmallView ? '60cqh' : '70cqh' }), PortalDiv, quarantinedItemsDialog] }, "ED")] }, "nwcm_E")] }));
};
